import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';
import Modal from '../components/Modal';
import { getChannelReports, uploadChannelReport, deleteChannelReport } from '../utils/api';

const ChannelReport = () => {
  usePageTitle('Channel Report');
  const navigate = useNavigate();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    from_date: '',
    to_date: '',
    file: null
  });

  const fetchReports = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await getChannelReports({ page, limit: pagination.limit });
      setReports(response.data.reports);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load channel reports');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadForm.from_date || !uploadForm.to_date) {
      toast.error('Please select both from and to dates');
      return;
    }

    if (!uploadForm.file) {
      toast.error('Please select an Excel file to upload');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('from_date', uploadForm.from_date);
      formData.append('to_date', uploadForm.to_date);
      formData.append('file', uploadForm.file);

      await uploadChannelReport(formData);
      toast.success('Channel report uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({ from_date: '', to_date: '', file: null });
      fetchReports();
    } catch (error) {
      console.error('Error uploading report:', error);
      toast.error(error.response?.data?.error || 'Failed to upload channel report');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;

    try {
      await deleteChannelReport(reportToDelete.id);
      toast.success('Channel report deleted successfully');
      setShowDeleteModal(false);
      setReportToDelete(null);
      fetchReports(pagination.page);
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete channel report');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'xlsm'].includes(ext)) {
        toast.error('Please select an Excel file (.xlsx, .xls, or .xlsm)');
        e.target.value = '';
        return;
      }
      setUploadForm({ ...uploadForm, file });
    }
  };

  const closeUploadModal = () => {
    if (!uploading) {
      setShowUploadModal(false);
      setUploadForm({ from_date: '', to_date: '', file: null });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Channel Reports</h1>
          <p className="page-subtitle">Upload and view sales channel performance reports</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Upload Report
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Loading reports...</span>
          </div>
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <ChartBarIcon className="empty-state-icon" />
            <p className="empty-state-title">No channel reports</p>
            <p>Upload your first channel report to get started.</p>
            <button className="btn btn-primary" onClick={() => setShowUploadModal(true)} style={{ marginTop: '1rem' }}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Upload Report
            </button>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Rows</th>
                    <th style={{ textAlign: 'right' }}>Total Earned</th>
                    <th>Uploaded By</th>
                    <th>Upload Date</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <strong>
                          {formatDate(report.from_date)} - {formatDate(report.to_date)}
                        </strong>
                      </td>
                      <td>{report.row_count}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                        {parseFloat(report.total_earned || 0).toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CZK
                      </td>
                      <td>{report.uploaded_by_name || '-'}</td>
                      <td>{formatDateTime(report.uploaded_at)}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => navigate(`/customers/channel-report/${report.id}`)}
                            title="View Report"
                          >
                            <EyeIcon style={{ width: 16, height: 16 }} />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => {
                              setReportToDelete(report);
                              setShowDeleteModal(true);
                            }}
                            title="Delete Report"
                            style={{ color: 'var(--color-accent-danger)' }}
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
                </span>
                <button
                  className="pagination-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchReports(pagination.page - 1)}
                >
                  ‹
                </button>
                {(() => {
                  const pages = [];
                  const start = Math.max(1, pagination.page - 2);
                  const end = Math.min(pagination.totalPages, start + 4);
                  for (let i = start; i <= end; i++) pages.push(i);
                  return pages.map((pageNum) => (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => fetchReports(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
                <button
                  className="pagination-btn"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchReports(pagination.page + 1)}
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={closeUploadModal}
        title="Upload Channel Report"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeUploadModal} disabled={uploading}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading || !uploadForm.file}
            >
              {uploading ? 'Uploading...' : 'Upload Report'}
            </button>
          </>
        }
      >
        <form onSubmit={handleUpload}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">From Date *</label>
              <input
                type="date"
                className="form-input"
                value={uploadForm.from_date}
                onChange={(e) => setUploadForm({ ...uploadForm, from_date: e.target.value })}
                required
                disabled={uploading}
              />
            </div>
            <div className="form-group">
              <label className="form-label">To Date *</label>
              <input
                type="date"
                className="form-input"
                value={uploadForm.to_date}
                onChange={(e) => setUploadForm({ ...uploadForm, to_date: e.target.value })}
                required
                disabled={uploading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Excel File *</label>
            <div className="file-upload-area">
              <input
                type="file"
                accept=".xlsx,.xls,.xlsm"
                onChange={handleFileChange}
                disabled={uploading}
                className="file-input"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="file-upload-label">
                <DocumentArrowUpIcon style={{ width: 40, height: 40, color: 'var(--color-text-tertiary)', marginBottom: '0.5rem' }} />
                <span style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                  {uploadForm.file ? uploadForm.file.name : 'Click to select or drag and drop'}
                </span>
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginTop: '0.25rem' }}>
                  Supported formats: .xlsx, .xls, .xlsm
                </span>
              </label>
            </div>
          </div>

          {uploading && (
            <div style={{ marginTop: '1rem' }}>
              <div className="progress-bar">
                <div className="progress-bar-fill progress-bar-indeterminate"></div>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', textAlign: 'center', marginTop: '0.5rem' }}>
                Uploading and processing file...
              </p>
            </div>
          )}
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Channel Report"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete Report
            </button>
          </>
        }
      >
        <div style={{ textAlign: 'center' }}>
          <ExclamationTriangleIcon style={{ width: 48, height: 48, color: 'var(--color-accent-danger)', margin: '0 auto 1rem' }} />
          {reportToDelete && (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Are you sure you want to delete the report for period{' '}
              <strong style={{ color: 'var(--color-text-primary)' }}>
                {formatDate(reportToDelete.from_date)} - {formatDate(reportToDelete.to_date)}
              </strong>
              ? This will also delete all {reportToDelete.row_count} rows and cannot be undone.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ChannelReport;
